import type { Group, Object3D, Scene } from "three";

import type { Engine } from "../../../engine/Engine";
import type { LoadedModelHandle } from "../../model/LoadedModelHandle";
import type { ModelLoadSession } from "../../model/ModelLoadSession";
import type { ModelSource } from "../../model/ModelSource";
import {
  CommandRegistry,
  type CommandContract,
  createDisposable,
  type Disposable,
  DisposableGroup,
  ObservableStore,
  TypedEventHub,
} from "./index";

export interface ProductLoadingEvent {
  source: ModelSource;
  session: ModelLoadSession;
  progress?: number;
}

export interface ProductLoadedEvent {
  source: ModelSource;
  handle: LoadedModelHandle;
  holder: Group;
}

export interface ProductRemovedEvent {
  handle?: LoadedModelHandle;
}

export interface ProductErrorEvent {
  source: ModelSource;
  error: unknown;
}

export interface ProductEventMap {
  loading: ProductLoadingEvent;
  loaded: ProductLoadedEvent;
  removed: ProductRemovedEvent;
  ready: undefined;
  error: ProductErrorEvent;
}

export interface ProductCommandMap {
  load: CommandContract<ModelSource, LoadedModelHandle | undefined>;
  remove: CommandContract<void, void>;
}

export interface CameraEventMap {
  changed: undefined;
  interaction: { active: boolean };
}

export interface CameraCommandMap {
  fit: CommandContract<Object3D, void>;
}

export interface RenderEventMap {
  resized: undefined;
  sceneMutated: { scene?: Scene };
  invalidated: { reason: string };
}

export interface RenderCommandMap {
  invalidate: CommandContract<string, void>;
  resize: CommandContract<boolean | undefined, void>;
  resetPathTracing: CommandContract<void, void>;
}

export type PathTraceStatus = "inactive" | "preparing" | "active" | "error";

export interface PathTraceState {
  status: PathTraceStatus;
  progress: number;
  samples: number;
  error?: string;
}

export class ViewerRuntimeScope {
  readonly product = {
    events: new TypedEventHub<ProductEventMap>(),
    commands: new CommandRegistry<ProductCommandMap>(),
  };

  readonly camera = {
    events: new TypedEventHub<CameraEventMap>(),
    commands: new CommandRegistry<CameraCommandMap>(),
  };

  readonly render = {
    events: new TypedEventHub<RenderEventMap>(),
    commands: new CommandRegistry<RenderCommandMap>(),
  };

  readonly pathTracing = new ObservableStore<PathTraceState>({
    status: "inactive",
    progress: 0,
    samples: 0,
  });

  private readonly disposers = new DisposableGroup();

  constructor(readonly engine: Engine) {
    this.disposers.add(
      this.listenEngine("engine-resize", () => {
        this.render.events.emit("resized");
      }),
    );
    this.disposers.add(
      this.listenEngine("update-camera", () => {
        this.camera.events.emit("changed");
      }),
    );
    this.disposers.add(
      this.listenEngine("interaction-start", () => {
        this.camera.events.emit("interaction", { active: true });
      }),
    );
    this.disposers.add(
      this.listenEngine("interaction-end", () => {
        this.camera.events.emit("interaction", { active: false });
      }),
    );
    this.disposers.add(
      this.listenEngine("scene-mutated", (event) => {
        this.render.events.emit("sceneMutated", {
          scene: (event as CustomEvent<Scene>).detail,
        });
      }),
    );
    this.disposers.add(
      this.render.commands.register("invalidate", (reason) => {
        this.render.events.emit("invalidated", { reason });
        this.engine.dispatchCustomEvent("request-update");
      }),
    );
    this.disposers.add(
      this.render.commands.register("resize", (force = true) => {
        this.engine.resize(force);
      }),
    );
  }

  dispose(): void {
    this.disposers.dispose();
    this.product.events.dispose();
    this.product.commands.dispose();
    this.camera.events.dispose();
    this.camera.commands.dispose();
    this.render.events.dispose();
    this.render.commands.dispose();
    this.pathTracing.dispose();
  }

  private listenEngine(
    type: string,
    listener: EventListenerOrEventListenerObject,
  ): Disposable {
    this.engine.addEventListener(type, listener);
    return createDisposable(() => this.engine.removeEventListener(type, listener));
  }
}

const runtimeScopes = new WeakMap<Engine, ViewerRuntimeScope>();

export function getViewerRuntimeScope(engine: Engine): ViewerRuntimeScope {
  let scope = runtimeScopes.get(engine);
  if (!scope) {
    scope = new ViewerRuntimeScope(engine);
    runtimeScopes.set(engine, scope);
  }
  return scope;
}
